import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import Salary from './Salary';
import Stocks from './Stocks';
import Expenses from './Expenses';
import Savings from './Savings';
import Liabilities from './Liabilities';
import AdminPanel from './AdminPanel';
import { ThemeContext, DARK, LIGHT } from '../ThemeContext';
import Chatbot from '../components/Chatbot';

/* ─────────────────────────────────────────────────────────────────────────────
   U-JARVIS FINANCIAL COMMAND CENTER — v4.0 ULTRA PREMIUM
   Dark / Light theme — toggle in top bar, synced via ThemeContext to all pages
───────────────────────────────────────────────────────────────────────────── */

const F = "'Fira Code','JetBrains Mono',monospace";

/* gradient helpers — recomputed from active theme */
const makeG = (T) => ({
  sky:     `linear-gradient(135deg,${T.sky},${T.indigo})`,
  violet:  `linear-gradient(135deg,${T.violet},#ec4899)`,
  emerald: `linear-gradient(135deg,${T.emerald},${T.cyan})`,
  amber:   `linear-gradient(135deg,${T.amber},${T.rose})`,
  rose:    `linear-gradient(135deg,${T.rose},${T.violet})`,
});

/* ─── Sparkline ─── */
const Spark = ({ data, color, w=100, h=32 }) => {
  if (!data || data.length < 2) return <div style={{height:h}}/>;
  const max=Math.max(...data); const min=Math.min(...data); const r=max-min||1;
  const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-min)/r)*(h-4)-2}`).join(' ');
  const id=`sp${color.replace(/[^a-z0-9]/gi,'')}`;
  return (
    <svg width={w} height={h} style={{display:'block',overflow:'visible'}}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${id})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round"/>
      {(() => { const last=pts.split(' ').pop().split(','); return <circle cx={last[0]} cy={last[1]} r="2.5" fill={color}/>; })()}
    </svg>
  );
};

/* ─── Donut ─── */
const Donut = ({ segs, size=88, sw=9, bgColor }) => {
  const r=(size-sw)/2; const circ=2*Math.PI*r;
  const tot=segs.reduce((s,g)=>s+Math.max(g.v,0),0)||1;
  let off=0;
  return (
    <svg width={size} height={size} style={{transform:'rotate(-90deg)',flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={bgColor||'rgba(255,255,255,0.04)'} strokeWidth={sw}/>
      {segs.filter(g=>g.v>0).map((g,i)=>{
        const len=(g.v/tot)*circ; const el=(
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={g.c} strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={`${len} ${circ-len}`} strokeDashoffset={-off}
            style={{transition:'all .7s ease'}}/>
        ); off+=len; return el;
      })}
    </svg>
  );
};

/* ─── Animated number ─── */
const Num = ({ val, prefix='₹', decimals=0, color, roseColor }) => {
  const str = val < 0
    ? `-${prefix}${Math.abs(val).toLocaleString('en-IN',{minimumFractionDigits:decimals})}`
    : `${prefix}${val.toLocaleString('en-IN',{minimumFractionDigits:decimals})}`;
  return <span style={{color: color||(val<0?(roseColor||'#f43f5e'):'inherit')}}>{str}</span>;
};

/* ─── Bar Chart ─── */
const BarChart = ({ bars, color, negColor, height=90 }) => {
  const max=Math.max(...bars.map(b=>Math.abs(b.v)),1);
  return (
    <div style={{display:'flex',alignItems:'center',gap:3,height,padding:'0 2px'}}>
      {bars.map((b,i)=>{
        const pct=(Math.abs(b.v)/max)*100; const pos=b.v>=0;
        return (
          <div key={i} style={{flex:1,height:'100%',display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',position:'relative'}} title={b.label||b.v}>
            {pos ? (
              <>
                <div style={{flex:1,width:'100%',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                  <div style={{width:'70%',margin:'0 auto',background:`linear-gradient(180deg,${color},${color}44)`,borderRadius:'3px 3px 0 0',height:`${pct}%`,minHeight:2,transition:'height .4s ease'}}/>
                </div>
                <div style={{height:'50%',width:'100%'}}/>
              </>
            ) : (
              <>
                <div style={{height:'50%',width:'100%'}}/>
                <div style={{flex:1,width:'100%',display:'flex',flexDirection:'column'}}>
                  <div style={{width:'70%',margin:'0 auto',background:`linear-gradient(0deg,${negColor},${negColor}44)`,borderRadius:'0 0 3px 3px',height:`${pct}%`,minHeight:2,transition:'height .4s ease'}}/>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Horizontal progress bar ─── */
const HBar = ({ pct, color, height=5, bgColor }) => (
  <div style={{width:'100%',height,background:bgColor||'rgba(255,255,255,0.04)',borderRadius:999}}>
    <div style={{height,borderRadius:999,width:`${Math.min(pct,100)}%`,background:color,transition:'width .6s ease',boxShadow:`0 0 8px ${color}55`}}/>
  </div>
);

/* ═══════════════════════════════════════
   THEME TOGGLE BUTTON
═══════════════════════════════════════ */
const ThemeToggle = ({ isDark, onToggle }) => (
  <button
    onClick={onToggle}
    title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    style={{
      display:'flex', alignItems:'center', gap:8,
      background: isDark ? 'rgba(255,215,0,0.08)' : 'rgba(99,102,241,0.08)',
      border: isDark ? '1px solid rgba(255,215,0,0.20)' : '1px solid rgba(99,102,241,0.20)',
      borderRadius:9, padding:'7px 13px', cursor:'pointer',
      transition:'all .25s', fontFamily:F,
    }}
    onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.05)';}}
    onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';}}
  >
    <span style={{fontSize:15,lineHeight:1,transition:'transform .4s',display:'inline-block',
      transform: isDark ? 'rotate(0deg)' : 'rotate(180deg)'}}>
      {isDark ? '☀️' : '🌙'}
    </span>
    <span style={{fontSize:8,letterSpacing:'0.3em',fontWeight:700,color: isDark ? '#FFD700' : '#4f46e5'}}>
      {isDark ? 'LIGHT' : 'DARK'}
    </span>
  </button>
);

/* ══════════════════════════════════════
   MAIN DASHBOARD COMPONENT
══════════════════════════════════════ */
/* ── responsive hook ── */
const useWindowWidth = () => {
  const [w, setW] = useState(typeof window!=='undefined'?window.innerWidth:1200);
  useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return w;
};

const Dashboard = ({ user }) => {
  const [tab, setTab]             = useState('DASHBOARD');
  const [loading, setLoading]     = useState(true);
  const [fbOk, setFbOk]           = useState(null);
  const [time, setTime]           = useState(new Date());
  const [sideHover, setSideHover] = useState(null);
  const [isDark, setIsDark]       = useState(true);   // ← theme toggle

  const winW = useWindowWidth();
  const isMobile = winW < 768;
  const isTablet = winW < 1024;

  const T  = isDark ? DARK : LIGHT;
  const G  = makeG(T);

  const NAV = [
    { key:'DASHBOARD', label:'Dashboard',  icon:'⬡',  color:T.sky,     grad:G.sky },
    { key:'SALARY',    label:'Salary',     icon:'₿',  color:T.violet,  grad:G.violet },
    { key:'STOCKS',    label:'Stocks',     icon:'◈',  color:T.emerald, grad:G.emerald },
    { key:'EXPENSES',    label:'Expenses',     icon:'◎',  color:T.rose,    grad:G.rose },
    { key:'SAVINGS',     label:'Savings',      icon:'◆',  color:T.amber,   grad:G.amber },
    { key:'LIABILITIES', label:'Cards & EMIs', icon:'💳', color:T.rose,    grad:G.rose },
    ...(user?.email === 'admin@u-jarvis.com' ? [{ key:'ADMIN', label:'Admin Center', icon:'🛡️', color:T.rose, grad:G.rose }] : []),
  ];

  const [D, setD] = useState({
    salaryBalance:0, pfTotal:0, baseSalary:0, pfAmountSet:0,
    salaryHistory:[], bonuses:[],
    stockOrders:[], stocksTodayProfit:0, stocksTotalProfit:0,
    expenses:[], totalExpenses:0,
    savingsEntries:[], totalSavings:0,
    cards:[], loans:[],
  });

  useEffect(()=>{ const t=setInterval(()=>setTime(new Date()),1000); return()=>clearInterval(t); },[]);

  useEffect(()=>{
    const load=async()=>{
      try{
        let [s,st,ex,sv,liab]=await Promise.all([
          getDoc(doc(db,'users',user.uid,'data','salary')),
          getDoc(doc(db,'users',user.uid,'data','stocks')),
          getDoc(doc(db,'users',user.uid,'data','expenses')),
          getDoc(doc(db,'users',user.uid,'data','savings')),
          getDoc(doc(db,'users',user.uid,'data','liabilities')),
        ]);

        // MIGRATION: Restore legacy data for Uday if new account is empty
        if (user.email === 'uday_kanchi@u-jarvis.com' && (!ex.exists() || (ex.data().items && ex.data().items.length === 0))) {
          console.log("Migrating legacy data for Uday...");
          const cols = ['salary', 'stocks', 'expenses', 'savings', 'liabilities'];
          for (const c of cols) {
             const oldSnap = await getDoc(doc(db, 'u-jarvis-data', c));
             if (oldSnap.exists()) {
               await setDoc(doc(db, 'users', user.uid, 'data', c), oldSnap.data());
             }
          }
          // Reload fresh data
          [s,st,ex,sv,liab]=await Promise.all([
            getDoc(doc(db,'users',user.uid,'data','salary')),
            getDoc(doc(db,'users',user.uid,'data','stocks')),
            getDoc(doc(db,'users',user.uid,'data','expenses')),
            getDoc(doc(db,'users',user.uid,'data','savings')),
            getDoc(doc(db,'users',user.uid,'data','liabilities')),
          ]);
        }

        const sd=s.data()||{}; const std=st.data()||{};
        const exd=ex.data()||{}; const svd=sv.data()||{};
        const liabd=liab.data()||{};
        const today=new Date().toISOString().slice(0,10);
        const tP=(std.orders||[]).filter(o=>o.date?.slice(0,10)===today).reduce((a,o)=>a+(o.profit||0),0);
        setD({
          salaryBalance:sd.currentBalance||0, pfTotal:sd.pfTotal||0,
          baseSalary:sd.baseSalary||0, pfAmountSet:sd.pfAmount||0,
          salaryHistory:sd.salaryHistory||[], bonuses:sd.bonuses||[],
          stockOrders:std.orders||[], stocksTodayProfit:tP, stocksTotalProfit:std.totalProfit||0,
          expenses:exd.items||[], totalExpenses:exd.totalExpenses||0,
          savingsEntries:svd.entries||[], totalSavings:svd.totalSavings||0,
          cards:liabd.cards||[], loans:liabd.loans||[],
        });
        setFbOk(true);
      }catch(e){ setFbOk(false); console.error(e); }
      setLoading(false);
    };
    load();
  },[user]);

  const handleUpdate=useCallback(p=>setD(prev=>({...prev,...p})),[]);

  /* computed — balances already have expenses deducted, so do NOT subtract totalExpenses again */
  const activeDebt = (D.cards||[]).reduce((s,c)=>s+c.used,0) + (D.loans||[]).reduce((s,l)=>s+l.principal,0);
  const netWorth = D.salaryBalance+D.pfTotal+D.stocksTotalProfit+D.totalSavings-activeDebt;
  const salaryIncome = D.salaryBalance+(D.bonuses||[]).reduce((s,b)=>s+b.amount,0);
  const stocksIncome = D.stocksTotalProfit;
  const spkSalary= (D.salaryHistory||[]).slice(-8).map(h=>h.balance||0);
  const spkStock = (D.stockOrders||[]).slice(-12).map(o=>o.profit||0);
  const spkSave  = (D.savingsEntries||[]).slice(-8).map(e=>e.amount||0);
  const stockBars= (() => {
    const map={}; (D.stockOrders||[]).forEach(o=>{const d=o.date?.slice(0,10);if(d)map[d]=(map[d]||0)+o.profit;});
    return Object.entries(map).slice(-18).map(([date,v])=>({v,label:date.slice(5)}));
  })();
  const winRate= (D.stockOrders||[]).length
    ? Math.round(((D.stockOrders||[]).filter(o=>o.profit>=0).length/(D.stockOrders||[]).length)*100)
    : 0;
  const expBySalary=(D.expenses||[]).filter(e=>e.source==='salary').reduce((s,e)=>s+e.amount,0);
  const expByStocks=(D.expenses||[]).filter(e=>e.source==='stocks').reduce((s,e)=>s+e.amount,0);
  const savTarget=1000000;
  const savPct=Math.min((D.totalSavings/savTarget)*100,100);
  const expCats=(()=>{
    const m={}; (D.expenses||[]).forEach(e=>{m[e.item]=(m[e.item]||0)+e.amount;});
    return Object.entries(m).sort((a,b)=>b[1]-a[1]).slice(0,4);
  })();
  const expMax=expCats[0]?.[1]||1;
  const activity=[
    ...(D.expenses||[]).slice(-4).map(e=>({icon:'↘',label:e.item,val:-e.amount,color:T.rose,date:e.date,tag:'EXP'})),
    ...(D.stockOrders||[]).slice(-4).map(o=>({icon:o.profit>=0?'↗':'↘',label:`₹${o.amount} order`,val:o.profit,color:o.profit>=0?T.emerald:T.rose,date:o.date,tag:'STK'})),
    ...(D.savingsEntries||[]).slice(-2).map(s=>({icon:'◆',label:s.note||'Savings',val:s.amount,color:T.amber,date:s.date,tag:'SAV'})),
  ].sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,7);

  /* theme-aware shared styles */
  const sCard = {
    borderRadius:14, padding:'20px 22px',
    background: T.cardGrad, border:`1px solid ${T.border}`,
    boxShadow: isDark ? 'none' : '0 2px 16px rgba(0,0,0,0.06)',
    transition:'background .4s,border .4s,box-shadow .4s',
  };
  const sHdr = {
    fontSize:9, letterSpacing:'0.3em', color:T.slate, fontFamily:F,
    marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${T.borderHd}`,
    display:'flex', justifyContent:'space-between', alignItems:'center', fontWeight:700,
  };
  const empty = { textAlign:'center', color:T.slate, fontSize:10, padding:'28px 0', letterSpacing:'0.25em', opacity:0.6 };

  /* ── Loading ── */
  if(loading) return (
    <div style={{height:'100vh',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:20,fontFamily:F}}>
      <div style={{display:'flex',gap:5}}>
        {[...Array(5)].map((_,i)=>(
          <div key={i} style={{width:4,height:28,background:G.sky,borderRadius:4,animation:`lbar .9s ease ${i*.12}s infinite`}}/>
        ))}
      </div>
      <div style={{fontSize:10,letterSpacing:'0.5em',color:T.slate}}>INITIALIZING COMMAND CENTER</div>
      <style>{`@keyframes lbar{0%,100%{transform:scaleY(.3);opacity:.3}50%{transform:scaleY(1);opacity:1}}`}</style>
    </div>
  );

  const activeNav=NAV.find(n=>n.key===tab);

  return (
    <ThemeContext.Provider value={{ isDark, T }}>
    <div style={{
      height:'100vh', display:'flex', flexDirection: isMobile?'column':'row', background:T.bg,
      fontFamily:F, color:T.text, overflow:'hidden', position:'relative',
      transition:'background .4s, color .4s',
    }}>

      {/* ════ AMBIENT BG GLOWS ════ */}
      <div style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:0}}>
        <div style={{position:'absolute',width:600,height:600,borderRadius:'50%',background:`radial-gradient(circle,${T.glow1} 0%,transparent 70%)`,top:-200,left:-100}}/>
        <div style={{position:'absolute',width:400,height:400,borderRadius:'50%',background:`radial-gradient(circle,${T.glow2} 0%,transparent 70%)`,bottom:0,right:100}}/>
        <div style={{position:'absolute',width:300,height:300,borderRadius:'50%',background:`radial-gradient(circle,${T.glow3} 0%,transparent 70%)`,top:'40%',left:'40%'}}/>
      </div>

      {/* ════ SIDEBAR (desktop only) ════ */}
      {!isMobile && (
      <aside style={{
        width:72, minWidth:72, height:'100vh', display:'flex', flexDirection:'column',
        background:T.sideBg, backdropFilter:'blur(20px)',
        borderRight:`1px solid ${T.borderHd}`,
        zIndex:100, position:'relative',
        boxShadow: isDark ? 'none' : '2px 0 20px rgba(0,0,0,0.06)',
        transition:'background .4s, border .4s',
      }}>
        <div style={{padding:'22px 0',display:'flex',justifyContent:'center',borderBottom:`1px solid ${T.borderHd}`}}>
          <div style={{width:38,height:38,borderRadius:10,background:G.sky,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:900,color:'#fff',letterSpacing:'-0.02em',boxShadow:`0 0 20px ${T.sky}44`}}>UJ</div>
        </div>

        <nav style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:6,padding:'16px 0'}}>
          {NAV.map(n=>{
            const active=tab===n.key;
            return (
              <div key={n.key}
                onClick={()=>setTab(n.key)}
                onMouseEnter={()=>setSideHover(n.key)}
                onMouseLeave={()=>setSideHover(null)}
                title={n.label}
                style={{
                  position:'relative',width:48,height:48,borderRadius:12,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  cursor:'pointer',transition:'all .22s',
                  background: active ? n.grad : sideHover===n.key ? (isDark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)') : 'transparent',
                  boxShadow: active ? `0 4px 20px ${n.color}40` : 'none',
                }}
              >
                {active && <div style={{position:'absolute',left:-1,top:'50%',transform:'translateY(-50%)',width:3,height:22,borderRadius:'0 3px 3px 0',background:n.grad,boxShadow:`0 0 8px ${n.color}`}}/>}
                <span style={{fontSize:18,transition:'transform .2s',transform:active||sideHover===n.key?'scale(1.1)':'scale(1)',filter:active?'brightness(0) invert(1)':'none',opacity:active?1:0.45}}>{n.icon}</span>
                {sideHover===n.key && !active && (
                  <div style={{position:'absolute',left:'calc(100% + 10px)',top:'50%',transform:'translateY(-50%)',background:T.ttipBg,backdropFilter:'blur(12px)',border:`1px solid ${n.color}22`,borderRadius:8,padding:'7px 14px',fontSize:9,letterSpacing:'0.2em',color:n.color,whiteSpace:'nowrap',boxShadow:`0 4px 20px rgba(0,0,0,0.15)`,zIndex:1000,pointerEvents:'none'}}>
                    {n.label.toUpperCase()}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div style={{padding:'16px 0',display:'flex',justifyContent:'center',borderTop:`1px solid ${T.borderHd}`}}>
          <div title="Logout"
            onClick={() => {
              if (window.confirm("INITIATE LOGOUT SEQUENCE? Confirm disconnection from the secure vault.")) {
                auth.signOut();
              }
            }}
            style={{width:38,height:38,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:.4,transition:'opacity .2s'}}
            onMouseEnter={e=>e.currentTarget.style.opacity=1}
            onMouseLeave={e=>e.currentTarget.style.opacity=.4}
          ><span style={{fontSize:16}}>⏻</span></div>
        </div>
      </aside>
      )}

      {/* ════ MAIN AREA ════ */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',zIndex:1}}>

        {/* ── Top Bar ── */}
        <header style={{height:isMobile?50:60,minHeight:isMobile?50:60,display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'0 14px':'0 28px',borderBottom:`1px solid ${T.borderHd}`,background:T.hdrBg,backdropFilter:'blur(16px)',boxShadow:isDark?'none':'0 2px 12px rgba(0,0,0,0.05)',transition:'background .4s, border .4s'}}>
          <div style={{display:'flex',alignItems:'center',gap:isMobile?8:14}}>
            {isMobile && <div style={{width:30,height:30,borderRadius:8,background:G.sky,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:900,color:'#fff',flexShrink:0}}>UJ</div>}
            <div>
              <div style={{fontSize:isMobile?12:15,fontWeight:800,letterSpacing:'0.04em',lineHeight:1,background:activeNav?.grad||G.sky,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
                {activeNav?.label?.toUpperCase() || 'DASHBOARD'}
              </div>
              {!isMobile && <div style={{fontSize:8,color:T.slate,letterSpacing:'0.3em',marginTop:3}}>U-JARVIS COMMAND CENTER</div>}
            </div>
          </div>

          <div style={{display:'flex',alignItems:'center',gap:isMobile?8:16}}>
            <ThemeToggle isDark={isDark} onToggle={()=>setIsDark(p=>!p)}/>

            {!isMobile && <div style={{display:'flex',alignItems:'center',gap:7}}>
              <div style={{width:6,height:6,borderRadius:'50%',background:fbOk===null?T.amber:fbOk?T.emerald:T.rose,boxShadow:`0 0 8px ${fbOk===null?T.amber:fbOk?T.emerald:T.rose}`,animation:fbOk===null?'pulse 1s infinite':'none'}}/>
              <span style={{fontSize:8,color:T.slate,letterSpacing:'0.15em'}}>{fbOk===null?'CONNECTING':fbOk?'FIREBASE ✓':'OFFLINE ✗'}</span>
            </div>}

            <div style={{textAlign:'right'}}>
              <div style={{fontSize:isMobile?11:14,fontWeight:700,color:T.sky,letterSpacing:'0.08em',lineHeight:1}}>
                {time.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false})}
              </div>
              {!isMobile && <div style={{fontSize:7,color:T.slate,letterSpacing:'0.15em',marginTop:2}}>
                {time.toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short',year:'numeric'}).toUpperCase()}
              </div>}
            </div>

            {!isMobile && <div style={{display:'flex',alignItems:'center',gap:9,padding:'7px 14px',borderRadius:9,background:isDark?'rgba(99,179,237,0.05)':'rgba(99,102,241,0.06)',border:`1px solid ${T.border}`}}>
              <div style={{width:28,height:28,borderRadius:8,background:G.violet,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:900,color:'#fff'}}>
                {user?.displayName ? user.displayName.substring(0, 2).toUpperCase() : 'UK'}
              </div>
              <div>
                <div style={{fontSize:9,color:T.text,fontWeight:700,letterSpacing:'0.1em'}}>{user?.displayName?.toUpperCase() || 'USER'}</div>
                <div style={{fontSize:7,color:T.emerald,letterSpacing:'0.2em'}}>● ACTIVE</div>
              </div>
            </div>}
          </div>
        </header>

        {/* ── Page Content ── */}
        <main style={{flex:1,overflowY:'auto',padding:isMobile?'14px 12px 80px':'22px 28px 28px',scrollbarWidth:'thin',scrollbarColor:`${T.scrollT} transparent`}}>
          <AnimatePresence mode="wait">
            <motion.div key={tab+String(isDark)}
              initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
              exit={{opacity:0,y:-10}} transition={{duration:.25,ease:'easeOut'}}>

              {/* ══════════════ DASHBOARD OVERVIEW ══════════════ */}
              {tab==='DASHBOARD' && (
                <div style={{maxWidth:1060,margin:'0 auto',width:'100%'}}>

                  {/* NET WORTH HERO */}
                  <div style={{borderRadius:isMobile?12:16,padding:isMobile?'18px 16px':'26px 30px',marginBottom:isMobile?12:18,position:'relative',overflow:'hidden',background:T.heroGrad,border:`1px solid ${T.heroBdr}`,boxShadow:isDark?'0 8px 40px rgba(0,0,0,0.3)':'0 4px 24px rgba(0,0,0,0.08)',transition:'all .4s'}}>
                    <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:G.sky}}/>
                    <div style={{position:'absolute',inset:0,opacity:isDark?0.025:0.04,backgroundImage:`repeating-linear-gradient(0deg,transparent,transparent 24px,${T.sky} 24px,${T.sky} 25px),repeating-linear-gradient(90deg,transparent,transparent 24px,${T.sky} 24px,${T.sky} 25px)`}}/>
                    <div style={{position:'relative',display:'flex',justifyContent:'space-between',alignItems:isMobile?'flex-start':'center',gap:isMobile?16:24,flexWrap:'wrap',flexDirection:isMobile?'column':'row'}}>
                      <div>
                        <div style={{fontSize:isMobile?7:8,letterSpacing:'0.5em',color:T.slate,marginBottom:8}}>TOTAL NET WORTH</div>
                        <div style={{fontSize:isMobile?24:36,fontWeight:900,letterSpacing:'.01em',lineHeight:1,marginBottom:10,color:T.text}}>
                          <Num val={netWorth} decimals={2} color={netWorth>=0?T.text:T.rose} roseColor={T.rose}/>
                        </div>
                        <div style={{display:'flex',gap:isMobile?12:20,flexWrap:'wrap'}}>
                          {[
                            {l:'SALARY INCOME',   v:`₹${salaryIncome.toLocaleString('en-IN')}`,       c:T.sky},
                            {l:'STOCKS INCOME',   v:`₹${stocksIncome.toLocaleString('en-IN')}`,       c:T.emerald},
                            {l:'TOTAL EXPENSE',   v:`₹${D.totalExpenses.toLocaleString('en-IN')}`,    c:T.rose},
                            {l:'ACTIVE DEBT',     v:`₹${activeDebt.toLocaleString('en-IN')}`,         c:T.rose},
                            {l:'PF CORPUS',       v:`₹${D.pfTotal.toLocaleString('en-IN')}`,          c:T.violet},
                          ].map(i=>(
                            <div key={i.l}>
                              <div style={{fontSize:7,color:T.slate,letterSpacing:'0.25em',marginBottom:3}}>{i.l}</div>
                              <div style={{fontSize:isMobile?11:13,fontWeight:700,color:i.c}}>{i.v}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {!isMobile && <div style={{display:'flex',alignItems:'center',gap:18}}>
                        <div style={{position:'relative'}}>
                          <Donut segs={[{v:D.salaryBalance,c:T.sky},{v:D.pfTotal,c:T.violet},{v:Math.max(D.stocksTotalProfit,0),c:T.emerald},{v:D.totalSavings,c:T.amber}]} size={90} sw={9} bgColor={T.donutBg}/>
                          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                            <div style={{fontSize:6,color:T.slate,letterSpacing:'0.2em'}}>ALLOC</div>
                          </div>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                          {[
                            {l:'Salary', c:T.sky,    v:D.salaryBalance},
                            {l:'PF',     c:T.violet, v:D.pfTotal},
                            {l:'Stocks', c:T.emerald,v:D.stocksTotalProfit},
                            {l:'Savings',c:T.amber,  v:D.totalSavings},
                          ].map(item=>(
                            <div key={item.l} style={{display:'flex',alignItems:'center',gap:8}}>
                              <div style={{width:6,height:6,borderRadius:1,background:item.c,flexShrink:0}}/>
                              <div style={{fontSize:8,color:T.slate,width:42}}>{item.l}</div>
                              <div style={{fontSize:9,color:item.c,fontWeight:700}}>₹{Math.max(item.v,0).toLocaleString('en-IN')}</div>
                            </div>
                          ))}
                        </div>
                      </div>}
                    </div>
                  </div>

                  {/* 5 METRIC CARDS */}
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':isTablet?'repeat(3,1fr)':'repeat(6,1fr)',gap:isMobile?8:12,marginBottom:isMobile?12:18}}>
                    {[
                      {title:'SALARY BAL.',   val:D.salaryBalance,     grad:G.sky,     color:T.sky,    spk:spkSalary,         icon:'₹', tabK:'SALARY'},
                      {title:'PF CORPUS',     val:D.pfTotal,           grad:G.violet,  color:T.violet, spk:[],                icon:'◈', tabK:'SALARY'},
                      {title:"TODAY'S P&L",   val:D.stocksTodayProfit, grad:G.emerald, color:D.stocksTodayProfit>=0?T.emerald:T.rose, spk:spkStock.slice(-6), icon:'↗', tabK:'STOCKS'},
                      {title:'TOTAL SAVINGS', val:D.totalSavings,      grad:G.amber,   color:T.amber,  spk:spkSave,           icon:'◆', tabK:'SAVINGS'},
                      {title:'EXPENDITURE',   val:D.totalExpenses,     grad:G.rose,    color:T.rose,   spk:[],                icon:'↘', tabK:'EXPENSES'},
                      {title:'ACTIVE DEBT',   val:activeDebt,          grad:G.rose,    color:T.rose,   spk:[],                icon:'💳', tabK:'LIABILITIES'},
                    ].map((c,i)=>(
                      <motion.div key={c.title}
                        initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:i*.07,duration:.3}}
                        onClick={()=>setTab(c.tabK)}
                        style={{borderRadius:14,padding:'18px 18px 14px',cursor:'pointer',background:T.cardGrad,border:`1px solid ${T.border}`,transition:'all .22s',position:'relative',overflow:'hidden',boxShadow:isDark?'none':'0 2px 12px rgba(0,0,0,0.05)'}}
                        onMouseOver={e=>{e.currentTarget.style.borderColor=c.color+'44';e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow=`0 8px 30px ${c.color}18`;}}
                        onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=isDark?'none':'0 2px 12px rgba(0,0,0,0.05)';}}
                      >
                        <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:c.grad}}/>
                        <div style={{position:'absolute',top:10,right:12,fontSize:34,opacity:.05,pointerEvents:'none'}}>{c.icon}</div>
                        <div style={{fontSize:7,letterSpacing:'0.35em',color:T.slate,marginBottom:10}}>{c.title}</div>
                        <div style={{fontSize:17,fontWeight:900,color:c.color,letterSpacing:'.01em',marginBottom:10,lineHeight:1}}>
                          ₹{Math.abs(c.val).toLocaleString('en-IN')}
                        </div>
                        {c.spk.length>=2 && <Spark data={c.spk} color={c.color} w={90} h={28}/>}
                      </motion.div>
                    ))}
                  </div>

                  {/* CHARTS ROW */}
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1.5fr 1fr',gap:isMobile?10:14,marginBottom:isMobile?12:18}}>
                    <div style={sCard}>
                      <div style={sHdr}>
                        <span>↗ STOCK P&amp;L HISTORY</span>
                        <div style={{display:'flex',gap:14,fontSize:8,color:T.slate,alignItems:'center'}}>
                          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:8,height:3,background:T.emerald,borderRadius:1,display:'inline-block'}}/> PROFIT</span>
                          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:8,height:3,background:T.rose,borderRadius:1,display:'inline-block'}}/> LOSS</span>
                        </div>
                      </div>
                      {stockBars.length>0
                        ? <BarChart bars={stockBars} color={T.emerald} negColor={T.rose} height={100}/>
                        : <div style={empty}>NO STOCK ORDERS YET</div>
                      }
                      {stockBars.length>0 && (
                        <div style={{display:'flex',justifyContent:'space-between',marginTop:8,fontSize:7,color:T.slate,letterSpacing:'0.1em'}}>
                          <span>{stockBars[0]?.label}</span>
                          <span style={{color:T.sky,fontWeight:700}}>WIN RATE: {winRate}%</span>
                          <span>{stockBars[stockBars.length-1]?.label}</span>
                        </div>
                      )}
                    </div>

                    <div style={sCard}>
                      <div style={sHdr}><span>↘ EXPENSE SOURCES</span></div>
                      <div style={{display:'flex',gap:20,alignItems:'center',margin:'8px 0 16px'}}>
                        <Donut segs={[{v:expBySalary,c:T.sky},{v:expByStocks,c:T.cyan}]} size={72} sw={8} bgColor={T.donutBg}/>
                        <div style={{flex:1,display:'flex',flexDirection:'column',gap:12}}>
                          {[{l:'FROM SALARY',c:T.sky,v:expBySalary},{l:'FROM STOCKS',c:T.cyan,v:expByStocks}].map(e=>(
                            <div key={e.l}>
                              <div style={{display:'flex',justifyContent:'space-between',fontSize:8,marginBottom:5}}>
                                <span style={{color:T.slate,letterSpacing:'0.2em'}}>{e.l}</span>
                                <span style={{color:e.c,fontWeight:700}}>₹{e.v.toLocaleString('en-IN')}</span>
                              </div>
                              <HBar pct={D.totalExpenses?((e.v/D.totalExpenses)*100):0} color={e.c} bgColor={T.hbarBg}/>
                            </div>
                          ))}
                        </div>
                      </div>
                      {expCats.length>0 && (
                        <>
                          <div style={{fontSize:7,letterSpacing:'0.3em',color:T.slate,marginBottom:10,marginTop:4,borderTop:`1px solid ${T.borderHd}`,paddingTop:12}}>TOP CATEGORIES</div>
                          {expCats.map(([cat,val],i)=>(
                            <div key={cat} style={{marginBottom:8}}>
                              <div style={{display:'flex',justifyContent:'space-between',fontSize:8,marginBottom:4}}>
                                <span style={{color:T.text,letterSpacing:'0.1em',textTransform:'uppercase'}}>{cat}</span>
                                <span style={{color:T.rose}}>₹{val.toLocaleString('en-IN')}</span>
                              </div>
                              <HBar pct={(val/expMax)*100} color={[T.rose,T.violet,T.sky,T.amber][i%4]} bgColor={T.hbarBg}/>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  {/* SAVINGS + ACTIVITY */}
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1.4fr',gap:isMobile?10:14}}>
                    <div style={sCard}>
                      <div style={sHdr}><span>◆ WEALTH VAULT</span></div>
                      <div style={{textAlign:'center',padding:'8px 0 16px'}}>
                        <div style={{fontSize:28,fontWeight:900,color:T.amber,marginBottom:4}}>₹{D.totalSavings.toLocaleString('en-IN')}</div>
                        <div style={{fontSize:8,color:T.slate,letterSpacing:'0.25em',marginBottom:16}}>OF ₹10,00,000 TARGET</div>
                        <div style={{position:'relative',width:90,height:90,margin:'0 auto 12px'}}>
                          <Donut segs={[{v:savPct,c:T.amber},{v:100-savPct,c:T.donutBg}]} size={90} sw={10} bgColor={T.donutBg}/>
                          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                            <div style={{fontSize:14,fontWeight:900,color:T.amber}}>{savPct.toFixed(1)}%</div>
                          </div>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                          <div style={{background:`${T.amber}0f`,border:`1px solid ${T.amber}20`,borderRadius:8,padding:'10px 8px'}}>
                            <div style={{fontSize:7,color:T.slate,letterSpacing:'0.25em',marginBottom:4}}>DEPOSITS</div>
                            <div style={{fontSize:14,fontWeight:700,color:T.amber}}>{(D.savingsEntries||[]).length}</div>
                          </div>
                          <div style={{background:`${T.emerald}0f`,border:`1px solid ${T.emerald}20`,borderRadius:8,padding:'10px 8px'}}>
                            <div style={{fontSize:7,color:T.slate,letterSpacing:'0.25em',marginBottom:4}}>REMAINING</div>
                            <div style={{fontSize:14,fontWeight:700,color:T.emerald}}>₹{Math.max(savTarget-D.totalSavings,0).toLocaleString('en-IN')}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={sCard}>
                      <div style={sHdr}><span>◉ RECENT ACTIVITY</span><span style={{fontSize:8,color:T.slate,fontWeight:400}}>{activity.length} ENTRIES</span></div>
                      {activity.length===0
                        ? <div style={empty}>NO RECENT ACTIVITY</div>
                        : activity.map((a,i)=>(
                          <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:i<activity.length-1?`1px solid ${T.actBdr}`:'none'}}>
                            <div style={{width:32,height:32,borderRadius:9,flexShrink:0,background:`${a.color}15`,border:`1px solid ${a.color}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:a.color}}>{a.icon}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:10,color:T.text,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.label}</div>
                              <div style={{fontSize:8,color:T.slate,letterSpacing:'0.15em'}}>{new Date(a.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})} · {a.tag}</div>
                            </div>
                            <div style={{fontSize:12,fontWeight:700,color:a.color,flexShrink:0}}>
                              {a.val>=0?'+':''}{a.val<0?'-':''} ₹{Math.abs(a.val).toLocaleString('en-IN')}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              )}

              {tab==='SALARY'      && <Salary      data={D} onUpdate={handleUpdate} user={user} />}
              {tab==='STOCKS'      && <Stocks      data={D} onUpdate={handleUpdate} user={user} />}
              {tab==='EXPENSES'    && <Expenses    data={D} onUpdate={handleUpdate} user={user} />}
              {tab==='SAVINGS'     && <Savings     data={D} onUpdate={handleUpdate} user={user} />}
              {tab==='LIABILITIES' && <Liabilities data={D} onUpdate={handleUpdate} user={user} />}
              {tab==='ADMIN'       && <AdminPanel  />}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* ── Bottom Status Bar (desktop) ── */}
        {!isMobile && <div style={{height:30,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 28px',borderTop:`1px solid ${T.borderHd}`,background:T.barBg,fontSize:8,color:T.slate,letterSpacing:'0.15em',flexShrink:0,transition:'background .4s, border .4s'}}>
          <span>U-JARVIS v4.0 // <span style={{color:T.amber}}>{user?.displayName?.toUpperCase() || 'USER'}</span></span>
          <span style={{display:'flex',alignItems:'center',gap:6,color:isDark?'rgba(255,215,0,0.5)':'rgba(99,102,241,0.6)',fontSize:7,letterSpacing:'0.25em'}}>
            {isDark ? '🌙 DARK MODE' : '☀️ LIGHT MODE'}
          </span>
          <span>SESSION <span style={{color:T.emerald}}>AUTHORIZED ✓</span></span>
        </div>}

        {/* ═══ MOBILE BOTTOM NAV BAR ═══ */}
        {isMobile && (
          <nav style={{
            position:'fixed',bottom:0,left:0,right:0,height:60,zIndex:200,
            display:'flex',alignItems:'center',justifyContent:'space-around',
            background:T.hdrBg,backdropFilter:'blur(20px)',
            borderTop:`1px solid ${T.borderHd}`,
            boxShadow:isDark?'0 -4px 20px rgba(0,0,0,0.4)':'0 -2px 16px rgba(0,0,0,0.08)',
          }}>
            {NAV.map(n=>{
              const active=tab===n.key;
              return (
                <div key={n.key} onClick={()=>setTab(n.key)}
                  style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,cursor:'pointer',padding:'6px 8px',borderRadius:10,
                    background:active?`${n.color}15`:'transparent',transition:'all .2s'}}
                >
                  <span style={{fontSize:18,opacity:active?1:0.4,transition:'opacity .2s',filter:active?'none':'none'}}>{n.icon}</span>
                  <span style={{fontSize:7,letterSpacing:'0.1em',color:active?n.color:T.slate,fontWeight:active?700:400,fontFamily:F}}>{n.label.slice(0,5).toUpperCase()}</span>
                  {active && <div style={{width:16,height:2,borderRadius:1,background:n.color,marginTop:1}}/>}
                </div>
              );
            })}
          </nav>
        )}
      </div>
      
      <Chatbot user={user} T={T} />

      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.25}}
        @keyframes lbar{0%,100%{transform:scaleY(.3);opacity:.3}50%{transform:scaleY(1);opacity:1}}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:${T.scrollT};border-radius:4px}
        ::-webkit-scrollbar-thumb:hover{background:${isDark?'rgba(99,179,237,0.25)':'rgba(99,102,241,0.3)'}}
      `}</style>
    </div>
    </ThemeContext.Provider>
  );
};

export default Dashboard;